import ReactTooltip from 'react-tooltip';
import { Link } from 'react-router-dom';
import { Network } from '@kattana-io/common';
import { PoolWidgetRow } from '../helpers/rows';
import SmallCurrencyIcon from '../../Trading/common/SmallCurrencyIcon';
import Text from '../../../atoms/Text';
import NumberDisplay from '../../../molecules/NumberDisplay';
import { formatUSD } from '../../Modal/TickersModal/helpers';
import {
  onCopyAddress,
  onTokenScan,
  onWalletScan,
} from '../../../helpers/addressUtils';
import { formatAddress } from '../../../helpers/formatters';
import { formatTimestamp } from '../../DexTrades/formatters';
import NumberAnimation from '../../../molecules/NumberAnimation';

import './styles.scss';

interface IPoolRowProps {
  row: PoolWidgetRow;
  network: Network;
  timezone?: string;
  goPlusSecurity?: boolean;
  toggleGoPlusSecurity?: () => void;
}

const PoolRow = ({
  row,
  network,
  timezone,
  goPlusSecurity,
  toggleGoPlusSecurity,
}: IPoolRowProps) => {
  const getRowValue = () => {
    switch (row.type) {
    case 'price':
      return (
        <NumberAnimation
          value={row.value}
          round={2}
          endSymbol={row.sign}
          textProps={{ size: 12, className: row.color }}
        />
      );
    case 'change':
      return (
        <div className='pool_row__group'>
          <NumberDisplay
            value={row.value}
            round={2}
            endSymbol='$'
            textProps={{ size: 12, className: row.color }}
          />
          <NumberDisplay
            value={row.percent}
            round={2}
            startSymbol='('
            endSymbol='%)'
            textProps={{ size: 12, className: row.color }}
          />
        </div>
      );
    case 'usd':
      return <Text size={12}>{formatUSD(row.value)}</Text>;
    case 'ticker_address':
    case 'wallet_address':
      return (
        <div className='pool_row__coin'>
          <Text
            className='pool_row__coin__address'
            size={12}
            style={{
              'data-for': `pool-row-tooltip-${row.id}`,
              'data-tip': row.value,
            }}
          >
            {formatAddress(row.value)}
          </Text>
          <div
            className='pool_row__coin__action icon-copy'
            onClick={() => onCopyAddress(row.value)}
          />
          <div
            className='pool_row__coin__action icon-top-right-square'
            onClick={() => onWalletScan(row.value, network.getSymbol())}
          />
        </div>
      );
    case 'coin_address':
      return (
        <div className='pool_row__coin'>
          <Text
            className='pool_row__coin__address'
            size={12}
            style={{
              'data-for': `pool-row-tooltip-${row.id}`,
              'data-tip': row.value,
            }}
          >
            {formatAddress(row.value)}
          </Text>
          <div
            className='pool_row__coin__action icon-copy'
            onClick={() => onCopyAddress(row.value)}
          />
          <div
            className='pool_row__coin__action icon-top-right-square'
            onClick={() => onTokenScan(row.value, network.getSymbol())}
          />
        </div>
      );
    case 'date':
      return <Text size={12}>{formatTimestamp(row.value, timezone)}</Text>;
    case 'issue':
      return (
        <div
          className={`pool_row__issue ${row.color} ${
            row.disabled ? 'pool_row__issue--disabled' : ''
          }`}
          onClick={toggleGoPlusSecurity}
        >
          {row.disabled ? (
            <Text size={12}>Token not found</Text>
          ) : (
            <>
              <Text size={12}>
                {row.value ? row.value : 'No'} Issue
                {!row.value || row.value > 1 ? 's' : ''}
              </Text>
              <div
                className={`pool_row__issue__icon ${
                  goPlusSecurity ? 'icon-eye-off' : 'icon-eye'
                } `}
              />
            </>
          )}
        </div>
      );
    case 'boolean':
      return (
        <div
          className={`pool_row__bool ${
            row.value ? 'icon-check' : 'icon-close'
          }`}
        />
      );
    case 'number':
      return (
        <NumberDisplay
          value={row.value}
          round={2}
          endSymbol={row.percent ? '%' : ''}
          textProps={{ size: 12 }}
        />
      );
    default:
      return <Text size={12}>{row.value}</Text>;
    }
  };

  const getRowLabel = () => {
    switch (row.type) {
    case 'coin_address':
      return (
        <Link
          to={`/en/token/${network?.getSymbol()}/${row.value}`}
          className='pool_row__group'
        >
          <SmallCurrencyIcon
            network={network}
            currency={row.label}
            contract={row.value}
            variant='s'
          />
          <Text size={12} className='text--grey-200 text--weight-500'>
            {row.label}
          </Text>
        </Link>
      );
    default:
      return (
        <div
          className={`pool_row__label ${
            row.tooltip ? 'pool_row__label--tooltip' : ''
          }`}
          {...(row.tooltip
            ? {
              'data-for': `pool-row-tooltip-${row.id}`,
              'data-tip': row.tooltip,
            }
            : {})}
        >
          <Text size={12} className='text--grey-200 text--weight-500'>
            {row.label}
          </Text>
          {row.tooltip && <hr className='pool_row__label__underline' />}
        </div>
      );
    }
  };

  return (
    <div className='pool_row'>
      {getRowLabel()}
      {getRowValue()}
      {(row.tooltip ||
        row.type === 'coin_address' ||
        row.type === 'ticker_address') && (
        <ReactTooltip
          id={`pool-row-tooltip-${row.id}`}
          place='bottom'
          effect='solid'
          className='text_tooltip pool_row__label_tooltip'
        />
      )}
    </div>
  );
};

export default PoolRow;
